import type { AnimationKeyframe, Easing, Property } from 'web';

type NumberProperty = Property<number>;
type NumberKeyframe = AnimationKeyframe<number>;

export type Fade =
  | { kind: 'constant'; fadedValue: number }
  | {
      kind: 'fade';
      fadeInEnabled: boolean;
      fadeOutEnabled: boolean;
      fadeInDuration?: number;
      fadeOutDuration?: number;
      fadedValue: number;
    }
  | { kind: 'unrecognized' };

const close = (a: number, b: number) => Math.abs(a - b) < 0.0001;

/** Reads a supported fade from keyframes. All-zero sequences have no identifiable fade direction. */
export function keyframesToFade(keyframes: NumberKeyframe[], duration: number): Fade {
  if (keyframes.length === 1) {
    const [keyframe] = keyframes;
    return Number.isFinite(keyframe.time) && Number.isFinite(keyframe.value)
      ? { kind: 'constant', fadedValue: keyframe.value }
      : { kind: 'unrecognized' };
  }
  if (
    keyframes.length < 2 ||
    keyframes.length > 4 ||
    !Number.isFinite(duration) ||
    duration <= 0 ||
    keyframes.some((keyframe) => !Number.isFinite(keyframe.time) || !Number.isFinite(keyframe.value)) ||
    !keyframes.every((keyframe, index) => index === 0 || keyframe.time > keyframes[index - 1].time) ||
    keyframes[0].time < 0 ||
    keyframes[keyframes.length - 1].time > duration
  ) {
    return { kind: 'unrecognized' };
  }
  if (keyframes.every((keyframe) => close(keyframe.value, 0))) return { kind: 'unrecognized' };

  const first = keyframes[0];
  const last = keyframes[keyframes.length - 1];
  // Boundary keyframes retain a disabled edge's timing even when its value is flat.
  // Two flat keyframes spanning the entire layer remain ambiguous.
  const startsAtLayerStart = close(first.time, 0);
  const endsAtLayerEnd = close(last.time, duration);
  const fadeIn = startsAtLayerStart && (keyframes.length >= 3 || !endsAtLayerEnd || close(first.value, 0));
  const fadeOut = endsAtLayerEnd && (keyframes.length >= 3 || !startsAtLayerStart || close(last.value, 0));
  const plateau = keyframes.slice(fadeIn ? 1 : 0, fadeOut ? -1 : undefined);
  const value = plateau[0]?.value;
  if (value === undefined || close(value, 0)) return { kind: 'unrecognized' };
  if (!plateau.every((keyframe) => close(keyframe.value, value))) return { kind: 'unrecognized' };
  if (!fadeIn && !fadeOut) return { kind: 'unrecognized' };
  if (plateau.length > 2) return { kind: 'unrecognized' };
  if (fadeIn && !close(first.value, 0) && !close(first.value, value)) return { kind: 'unrecognized' };
  if (fadeOut && !close(last.value, 0) && !close(last.value, value)) return { kind: 'unrecognized' };
  return {
    kind: 'fade',
    fadeInEnabled: fadeIn && close(first.value, 0),
    fadeOutEnabled: fadeOut && close(last.value, 0),
    ...(fadeIn ? { fadeInDuration: keyframes[1].time - first.time } : {}),
    ...(fadeOut ? { fadeOutDuration: last.time - keyframes[keyframes.length - 2].time } : {}),
    fadedValue: value,
  };
}

/** Reads a constant or supported fade from a numeric property. */
export function propertyToFade(property: NumberProperty, duration: number): Fade {
  if (property.type === 'constant') {
    return { kind: 'constant', fadedValue: property.value };
  }
  return keyframesToFade(property.keyframes, duration);
}

/** Preserves timing, easing and enabled state when editing a property's faded value. */
export function editFadedProperty(
  property: NumberProperty,
  duration: number,
  value: number,
): NumberProperty | undefined {
  const fade = propertyToFade(property, duration);
  if (fade.kind === 'unrecognized') return undefined;
  if (fade.kind === 'constant') return { type: 'constant', value };
  if (property.type !== 'animated') return undefined;
  const last = property.keyframes.length - 1;
  return {
    type: 'animated',
    keyframes: property.keyframes.map((keyframe, index) =>
      (fade.fadeInEnabled && index === 0) || (fade.fadeOutEnabled && index === last)
        ? keyframe
        : { ...keyframe, value },
    ),
  };
}

/** Moves a recognized fade's endpoints, optionally onto a new layer duration, preserving value and easing. */
export function editFadeDurations(
  property: NumberProperty,
  duration: number,
  fadeInDuration: number | undefined,
  fadeOutDuration: number | undefined,
  nextDuration = duration,
): NumberProperty | undefined {
  const fade = propertyToFade(property, duration);
  if (fade.kind !== 'fade' || property.type !== 'animated') return undefined;
  if (fadeInDuration === undefined && fadeOutDuration === undefined) return undefined;
  if (
    !Number.isFinite(nextDuration) ||
    nextDuration <= 0 ||
    (fadeInDuration !== undefined && (!Number.isFinite(fadeInDuration) || fadeInDuration <= 0)) ||
    (fadeOutDuration !== undefined && (!Number.isFinite(fadeOutDuration) || fadeOutDuration <= 0)) ||
    (fadeInDuration ?? 0) + (fadeOutDuration ?? 0) > nextDuration
  ) {
    return undefined;
  }

  const oldKeyframes = property.keyframes;
  const oldPlateau = oldKeyframes.slice(
    fade.fadeInDuration === undefined ? 0 : 1,
    fade.fadeOutDuration === undefined ? undefined : -1,
  );
  const firstPlateau = oldPlateau[0];
  const lastPlateau = oldPlateau[oldPlateau.length - 1];
  if (!firstPlateau || !lastPlateau) return undefined;

  const keyframes: NumberKeyframe[] = [];
  if (fadeInDuration !== undefined) {
    keyframes.push({
      ...oldKeyframes[0],
      time: 0,
      value: fade.fadeInDuration === undefined || fade.fadeInEnabled ? 0 : fade.fadedValue,
    });
    keyframes.push({ ...firstPlateau, time: fadeInDuration, value: fade.fadedValue });
  }
  if (fadeOutDuration !== undefined) {
    const fadeOutStart = nextDuration - fadeOutDuration;
    const previous = keyframes[keyframes.length - 1];
    if (!previous || fadeOutStart > previous.time + 0.000001) {
      keyframes.push({ ...lastPlateau, time: fadeOutStart, value: fade.fadedValue });
    } else {
      /** When dragging a fade handle until both fades meet, preserve the fade-out easing at their shared keyframe. */
      keyframes[keyframes.length - 1] = { ...previous, easing: lastPlateau.easing };
    }
    keyframes.push({
      ...oldKeyframes[oldKeyframes.length - 1],
      time: nextDuration,
      value: fade.fadeOutDuration === undefined || fade.fadeOutEnabled ? 0 : fade.fadedValue,
    });
  }
  return { type: 'animated', keyframes };
}

/** Neutralizes or restores one fade edge while retaining both edges' timing and easing. */
export function editFadeEnabled(
  property: NumberProperty,
  duration: number,
  edge: 'in' | 'out',
  enabled: boolean,
): NumberProperty | undefined {
  let source = property;
  let fade = propertyToFade(source, duration);
  if (fade.kind !== 'fade' || source.type !== 'animated') return undefined;
  if ((edge === 'in' ? fade.fadeInDuration : fade.fadeOutDuration) === undefined) return undefined;
  if ((edge === 'in' ? fade.fadeInEnabled : fade.fadeOutEnabled) === enabled) return source;

  // Two equal keyframes at both layer boundaries cannot reveal which one-sided
  // fade they represent. Leave a tiny plateau when disabling that shape so its
  // direction remains recognizable after persistence and reload.
  const oneSidedFadeSpansLayer =
    (fade.fadeInDuration !== undefined && fade.fadeOutDuration === undefined && close(fade.fadeInDuration, duration)) ||
    (fade.fadeOutDuration !== undefined && fade.fadeInDuration === undefined && close(fade.fadeOutDuration, duration));
  if (!enabled && oneSidedFadeSpansLayer) {
    const margin = Math.min(0.01, duration / 2);
    source =
      editFadeDurations(
        source,
        duration,
        fade.fadeInDuration === undefined ? undefined : duration - margin,
        fade.fadeOutDuration === undefined ? undefined : duration - margin,
      ) ?? source;
    fade = propertyToFade(source, duration);
    if (fade.kind !== 'fade' || source.type !== 'animated') return undefined;
  }

  const endpointIndex = edge === 'in' ? 0 : source.keyframes.length - 1;
  return {
    type: 'animated',
    keyframes: source.keyframes.map((keyframe, index) =>
      index === endpointIndex ? { ...keyframe, value: enabled ? 0 : fade.fadedValue } : keyframe,
    ),
  };
}

/** Reads the interpolation used by one edge of a recognized fade. */
export function fadeEasing(property: NumberProperty, duration: number, edge: 'in' | 'out'): Easing | undefined {
  const fade = propertyToFade(property, duration);
  if (fade.kind !== 'fade' || property.type !== 'animated') return undefined;
  if (edge === 'in') {
    return fade.fadeInDuration === undefined ? undefined : property.keyframes[0]?.easing;
  }
  return fade.fadeOutDuration === undefined ? undefined : property.keyframes.at(-2)?.easing;
}

/** Changes the interpolation used by one edge while preserving the fade's timing and values. */
export function editFadeEasing(
  property: NumberProperty,
  duration: number,
  edge: 'in' | 'out',
  easing: Easing,
): NumberProperty | undefined {
  const fade = propertyToFade(property, duration);
  if (fade.kind !== 'fade' || property.type !== 'animated') return undefined;
  const index = edge === 'in' ? 0 : property.keyframes.length - 2;
  if ((edge === 'in' && fade.fadeInDuration === undefined) || (edge === 'out' && fade.fadeOutDuration === undefined)) {
    return undefined;
  }
  return {
    type: 'animated',
    keyframes: property.keyframes.map((keyframe, keyframeIndex) =>
      keyframeIndex === index ? { ...keyframe, easing } : keyframe,
    ),
  };
}

/** Creates a one-sided or two-sided linear fade. */
export function createFadeProperty(
  value: number,
  duration: number,
  fadeInDuration: number | undefined,
  fadeOutDuration: number | undefined,
): NumberProperty | undefined {
  if (
    !Number.isFinite(duration) ||
    duration <= 0 ||
    !Number.isFinite(value) ||
    value <= 0 ||
    (fadeInDuration === undefined && fadeOutDuration === undefined) ||
    (fadeInDuration !== undefined && (!Number.isFinite(fadeInDuration) || fadeInDuration <= 0)) ||
    (fadeOutDuration !== undefined && (!Number.isFinite(fadeOutDuration) || fadeOutDuration <= 0)) ||
    (fadeInDuration ?? 0) + (fadeOutDuration ?? 0) > duration
  ) {
    return undefined;
  }
  const keyframes: NumberKeyframe[] = [];
  if (fadeInDuration !== undefined) {
    keyframes.push({ time: 0, value: 0, easing: 'linear' });
    keyframes.push({ time: fadeInDuration, value, easing: 'linear' });
  }
  if (fadeOutDuration !== undefined) {
    const fadeOutStart = duration - fadeOutDuration;
    const previous = keyframes[keyframes.length - 1];
    if (!previous || fadeOutStart > previous.time + 0.000001) {
      keyframes.push({ time: fadeOutStart, value, easing: 'linear' });
    }
    keyframes.push({ time: duration, value: 0, easing: 'linear' });
  }
  return { type: 'animated', keyframes };
}
